import React, { useState } from "react";
import { motion } from "framer-motion";
import { Stamp, MessageCircle, CheckCircle2, Phone, Users, FileCheck2, Clock3, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import PageTransition from "../components/PageTransition";
import { toast } from "sonner";

export default function Visa() {
    const [form, setForm] = useState({
        customer_name: "", customer_phone: "",
        nationality: "Türkiye", persons: 1, date_from: "", notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/service-requests", { service_type: "visa", ...form, date_from: form.date_from || null });
            setResult(data);
            toast.success("Vize talebiniz alındı!");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Talep gönderilemedi");
        } finally { setSubmitting(false); }
    };

    return (
        <PageTransition>
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="visa-page">
                <div className="text-center mb-10">
                    <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">VİZE İŞLEMLERİ</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-bone tracking-tight">Umre Vizesi</h1>
                    <p className="text-bone/70 mt-4 max-w-2xl mx-auto leading-relaxed">
                        Suudi Arabistan umre vizesi işlemlerinizi sizin adınıza hızlı ve güvenilir şekilde tamamlıyoruz.
                        <span className="text-gold font-semibold"> Fiyat ve evrak bilgisi için bizimle iletişime geçiniz.</span>
                    </p>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-10">
                    {[
                        { icon: Clock3, title: "Hızlı Sonuç", desc: "Çoğu başvuru 48-72 saatte sonuçlanır" },
                        { icon: FileCheck2, title: "Kolay Evrak", desc: "Pasaport + fotoğraf, gerisini biz hallederiz" },
                        { icon: ShieldCheck, title: "Güvenilir", desc: "Resmi acente iş ortaklarıyla işlem" },
                    ].map((f, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="border border-border_green bg-emerald_surface p-5 text-center">
                            <f.icon className="w-6 h-6 text-gold mx-auto mb-2" />
                            <div className="font-serif text-lg">{f.title}</div>
                            <div className="text-xs text-muted_green mt-1">{f.desc}</div>
                        </motion.div>
                    ))}
                </div>

                {result ? (
                    <div className="border border-gold/40 bg-emerald_surface p-8 text-center space-y-4" data-testid="visa-success">
                        <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
                        <h2 className="font-serif text-2xl">Talebiniz Alındı!</h2>
                        <p className="text-muted_green text-sm max-w-md mx-auto">Vize uzmanımız gerekli evraklar ve fiyat bilgisiyle en kısa sürede size dönüş yapacak.</p>
                        {result.whatsapp_url && (
                            <a href={result.whatsapp_url} target="_blank" rel="noreferrer" className="btn-gold !text-sm inline-flex" data-testid="visa-whatsapp-btn">
                                <MessageCircle className="w-4 h-4" /> WhatsApp'tan Yaz
                            </a>
                        )}
                        <div>
                            <button onClick={() => setResult(null)} className="text-xs text-muted_green underline hover:text-gold mt-2">Yeni talep oluştur</button>
                        </div>
                    </div>
                ) : (
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                        className="border border-gold/40 bg-emerald_surface p-6 lg:p-8 space-y-4"
                        data-testid="visa-form"
                    >
                        <div className="flex items-center gap-2 text-gold">
                            <Stamp className="w-5 h-5" />
                            <h2 className="font-serif text-2xl text-bone">Vize Talebi Oluştur</h2>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label-dark">Ad Soyad</label>
                                <input type="text" required className="input-dark" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="visa-name" />
                            </div>
                            <div>
                                <label className="label-dark"><Phone className="w-3 h-3 inline mr-1" /> Telefon / WhatsApp</label>
                                <input type="tel" required className="input-dark" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+90 5xx xxx xx xx" data-testid="visa-phone" />
                            </div>
                            <div>
                                <label className="label-dark">Uyruk</label>
                                <input type="text" className="input-dark" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="visa-nationality" />
                            </div>
                            <div>
                                <label className="label-dark"><Users className="w-3 h-3 inline mr-1" /> Kişi Sayısı</label>
                                <input type="number" min={1} max={100} required className="input-dark" value={form.persons} onChange={(e) => setForm({ ...form, persons: Number(e.target.value) })} data-testid="visa-persons" />
                            </div>
                        </div>
                        <div>
                            <label className="label-dark">Planlanan Seyahat Tarihi (opsiyonel)</label>
                            <input type="date" className="input-dark" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="visa-date" />
                        </div>
                        <div>
                            <label className="label-dark">Not (opsiyonel)</label>
                            <textarea rows={2} className="input-dark" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="visa-notes" />
                        </div>
                        <button type="submit" disabled={submitting} className="btn-gold w-full !py-3.5" data-testid="visa-submit-btn">
                            <MessageCircle className="w-4 h-4" /> {submitting ? "Gönderiliyor…" : "Fiyat İçin Bizimle İletişime Geçin"}
                        </button>
                    </motion.form>
                )}
            </section>
        </PageTransition>
    );
}
